/** Words for one item in a stage's list: what it is, how it arrived, and its state in a few words. Pure. */
import { isTheirs } from './flow-state';
import { causeWords, unitWord } from './flow-words';
import type { FlowItem, FlowUnit } from './flow-types';

const SOURCE: Record<string, string> = {
  api: 'by API',
  upload: 'browser upload',
  email: 'by email',
  folder: 'watched folder',
  scanner: 'Paige Scan',
  mobile: 'Paige Mobile',
  demo: 'sample pages',
};

/** How the item's batch arrived, as staff say it. */
export function sourceWord(source: string | null): string {
  if (!source) return 'arrival not recorded';
  return SOURCE[source] ?? source;
}

/** "Page", "Document", "Import", "Capture", "Batch": a unit is whatever its stage works on. */
export function itemKindWord(item: FlowItem, unit: FlowUnit): string {
  const word = item.kind === 'unit' ? unitWord(unit, 1) : item.kind;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export interface ItemState {
  tone: 'stuck' | 'slow' | 'held' | 'live' | 'plain';
  words: string;
  /** Whether Problems is where staff act on it. */
  problems: boolean;
}

/** The item's state as a chip: red only for a park that is staff's, amber for slow or retrying, neutral for a hold. */
export function itemStateOf(item: FlowItem): ItemState {
  if (item.class === 'parked') {
    const theirs = isTheirs(item.reason ?? '');
    return {
      tone: theirs ? 'held' : 'stuck',
      words: `Parked, ${theirs ? 'theirs' : 'ours'}: ${causeWords('ours', item.reason)}`,
      problems: true,
    };
  }
  if (item.class === 'held')
    return { tone: 'held', words: `Held: ${causeWords('hold', item.reason)}`, problems: false };
  if (item.class === 'retrying')
    return { tone: 'slow', words: causeWords('retry', item.reason), problems: false };
  if (item.class === 'working') return { tone: 'live', words: 'Being worked', problems: false };
  return item.slow
    ? { tone: 'slow', words: 'Slow: waiting longer than it should', problems: false }
    : { tone: 'plain', words: 'Ready, waiting its turn', problems: false };
}

/** What a hold by design waits on, in words that say nothing is wrong, and when its age is worth a word with the customer. */
export interface HoldStory {
  /** Who it waits on and what happens then. */
  waitingOn: string;
  /** What the customer sees on their side, when Paige shows them something. */
  customerSees: string | null;
  /** After this long it is worth nudging the customer: never red, never ours. */
  nudgeAfterSeconds: number;
  nudge: string;
}

const DAY = 86_400;

const HOLDS: Record<string, HoldStory> = {
  job_setup: {
    waitingOn:
      'Waiting on the customer to set up this job. Nothing is wrong and nothing for us to do: it is read, and charged, only after setup.',
    customerSees: 'Their jobs page shows this job as not set up yet.',
    nudgeAfterSeconds: 3 * DAY,
    nudge: 'Waiting over 3 days: worth reminding the customer to finish setting up the job.',
  },
  no_credits: {
    waitingOn:
      'Waiting on the customer to add pages. Nothing is wrong: it is read as soon as they do.',
    customerSees: 'Their account says pages ran out and documents are waiting.',
    nudgeAfterSeconds: 2 * DAY,
    nudge: 'Waiting over 2 days: worth reminding the customer to add pages.',
  },
  job_off: {
    waitingOn: 'Waiting on the customer to switch this job back on. Nothing is wrong.',
    customerSees: 'Their jobs page shows this job as switched off.',
    nudgeAfterSeconds: 7 * DAY,
    nudge: 'Waiting over a week: ask the customer whether they still want this job.',
  },
  person: {
    waitingOn: 'Waiting for someone at the company to review it, by design. Nothing for us to do.',
    customerSees: 'It is in their review queue.',
    nudgeAfterSeconds: 7 * DAY,
    nudge: 'Waiting over a week for review: worth a word with the customer.',
  },
};

/** The story of a hold by design; null for anything that is not one. */
export function holdStory(reason: string | null): HoldStory | null {
  return HOLDS[reason ?? ''] ?? null;
}

/** Whether a hold has waited long enough to be worth a word with the customer. */
export function wantsNudge(reason: string | null, seconds: number): boolean {
  const story = holdStory(reason);
  return story !== null && seconds >= story.nudgeAfterSeconds;
}
